import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
} from "@material-ui/core";
import Zoom from "@material-ui/core/Zoom";
import { Close } from "@material-ui/icons";
import { CustomModal } from "../../../../../styles/modal/styles";
import { ModalContent } from "../../../../../styles/box/styles";
import { OkButton } from "../../../../../styles/button/styles";
import ImageDropzone from "../../../../Common/Images/ImageDropzone";
import MultiSelectChipsDropdown from "../../../../Common/Dropdowns/MultiSelectChipsDropdown";
import appFetch from "../../../../../api/appFetch";
import { BrandsWithObjectTypeUrl } from "../../../../../constants/apiUrls";
import { getInitialFlowSettings } from "../utils/constants";
import { FlowSettingsField } from "../utils/flowSchema";
import { useFieldStyles } from "./fieldStyles";

export default function FlowSettingsModal({
  open,
  onClose,
  onSave,
  value,
  accessibleMoboOutlets = [],
  objectType,
  resources = {},
}) {
  const classes = useFieldStyles();
  const [draft, setDraft] = useState(getInitialFlowSettings());
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    if (open) setDraft({ ...getInitialFlowSettings(), ...value });
  }, [open, value]);

  useEffect(() => {
    if (!open || objectType === undefined || objectType === null) return;
    appFetch({
      url: BrandsWithObjectTypeUrl(objectType),
      onSuccess: (response) => setBrands(response || []),
      onError: () => {},
    });
  }, [open, objectType]);

  const set = (key, val) => setDraft((prev) => ({ ...prev, [key]: val }));

  const outletOptions = accessibleMoboOutlets.map((o) => ({
    key: o.id,
    name: o.description,
  }));
  const brandOptions = brands.map((b) => ({ key: b.id, name: b.description }));

  const handleSave = () => {
    onSave(draft);
    onClose();
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      disableBackdropClick
      disableRestoreFocus
      BackdropProps={{ style: { backgroundColor: "#0000004D" } }}
    >
      <Zoom in={open} timeout={350}>
        <ModalContent
          style={{
            width: 520,
            minHeight: 0,
            maxHeight: "85vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            flexShrink={0}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="h6">
              {resources["EditFlow"]}
            </Typography>
            <IconButton id="close-diagnostic-flow-settings" size="small" onClick={onClose}>
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <Box
            display="flex"
            flexDirection="column"
            style={{ gap: "16px", flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}
            py={1}
          >
            <TextField
              id="diagnostic-flow-settings-additional-info"
              label={resources["DiagnosticFlowProtocolAdditionalInfo"]}
              value={draft[FlowSettingsField.INFORMATION]}
              onChange={(e) => set(FlowSettingsField.INFORMATION, e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              multiline
              rows={3}
              InputLabelProps={{
                shrink: true,
                className: classes.fieldLabel,
                title: resources["DiagnosticFlowProtocolAdditionalInfo"],
              }}
              InputProps={{ className: classes.multilineInput }}
            />

            <TextField
              id="diagnostic-flow-settings-labourcodes"
              label={resources["DiagnosticFlowLinkedLabourcodes"]}
              value={draft[FlowSettingsField.LABOR_CODES]}
              onChange={(e) => set(FlowSettingsField.LABOR_CODES, e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              InputLabelProps={{
                className: classes.fieldLabel,
                title: resources["DiagnosticFlowLinkedLabourcodes"],
              }}
              inputProps={{ maxLength: 256 }}
              helperText={resources["DiagnosticFlowLabourcodesHelper"]}
            />

            <MultiSelectChipsDropdown
              id="diagnostic-flow-settings-outlets"
              label={resources["Outlets"]}
              options={outletOptions}
              value={draft[FlowSettingsField.OUTLET_IDS]}
              onChange={(v) => set(FlowSettingsField.OUTLET_IDS, v)}
            />

            <MultiSelectChipsDropdown
              id="diagnostic-flow-settings-brands"
              label={resources["Brands"]}
              options={brandOptions}
              value={draft[FlowSettingsField.BRAND_IDS]}
              onChange={(v) => set(FlowSettingsField.BRAND_IDS, v)}
            />

            <div style={{ display: "flex", flexDirection: "column" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="diagnostic-flow-settings-warranty"
                    size="small"
                    color="primary"
                    style={{ paddingTop: 4, paddingBottom: 4 }}
                    checked={draft[FlowSettingsField.IS_DEFAULT_FOR_WARRANTY]}
                    onChange={(e) => set(FlowSettingsField.IS_DEFAULT_FOR_WARRANTY, e.target.checked)}
                  />
                }
                label={resources["DiagnosticFlowDefaultForWarranty"]}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    id="diagnostic-flow-settings-is-active"
                    size="small"
                    color="primary"
                    style={{ paddingTop: 4, paddingBottom: 4 }}
                    checked={draft[FlowSettingsField.IS_ACTIVE]}
                    onChange={(e) => set(FlowSettingsField.IS_ACTIVE, e.target.checked)}
                  />
                }
                label={resources["IsActive"]}
              />
            </div>

            <div id="diagnostic-flow-settings-flowchart-image">
              <Typography variant="caption" color="textSecondary">
                {resources["DiagnosticFlowFlowchartImage"]}
              </Typography>
              <ImageDropzone
                resources={resources}
                alt="Flowchart"
                value={draft[FlowSettingsField.FLOWCHART_IMAGE]}
                onSelect={(file) => set(FlowSettingsField.FLOWCHART_IMAGE, file)}
                onRemove={() => set(FlowSettingsField.FLOWCHART_IMAGE, null)}
              />
            </div>
          </Box>

          <Box flexShrink={0} display="flex" justifyContent="flex-end" pt={1}>
            <OkButton id="diagnostic-flow-settings-save" onClick={handleSave}>
              {resources["Save"]}
            </OkButton>
          </Box>
        </ModalContent>
      </Zoom>
    </CustomModal>
  );
}
